// src/services/authService.ts
import api from './api';
import { AuthResponse, RegisterPayload, UserProfile, SendOtpPayload, VerifyOtpPayload, UserSession, ForgotPasswordPayload, VerifyResetCodePayload, ResetPasswordPayload, Eglise } from '../types/auth';

const AUTH_ENDPOINTS = {
  login: '/auth/login',
  register: '/auth/register',
  me: '/auth/me', // Corrected endpoint for fetching current user's profile
  deleteMe: '/auth/me', // Endpoint to delete own account
  sendOtp: '/auth/send-otp',
  verifyOtp: '/auth/verify-otp',
  refresh: '/auth/refresh', // Endpoint to refresh token
  logout: '/auth/logout', // Endpoint to logout
  logoutAll: '/auth/logout-all', // Endpoint to logout all devices
  sessions: '/auth/sessions', // Endpoint to get user sessions
  forgotPassword: '/auth/forgot-password', // Endpoint to request password reset
  verifyResetCode: '/auth/verify-reset-code', // Endpoint to verify reset code
  resetPassword: '/auth/reset-password', // Endpoint to reset password
  users: '/users', // Base endpoint for user-related operations
};

interface LoginPayload {
  email: string;
  password: string;
}

const authService = {
  async login(payload: LoginPayload): Promise<AuthResponse> {
    console.log('🌐 [authService] login called with payload:', payload);
    const response = await api.post<AuthResponse>(AUTH_ENDPOINTS.login, payload);
    console.log('✅ [authService] API response received:', response.data);
    const { access_token } = response.data;
    localStorage.setItem('access_token', access_token);
    console.log('💾 [authService] Token saved to localStorage');
    return response.data;
  },

  async register(payload: RegisterPayload): Promise<UserProfile> {
    const response = await api.post<UserProfile>(AUTH_ENDPOINTS.register, payload);
    return response.data;
  },

  async fetchMe(): Promise<UserProfile> {
    const response = await api.get<UserProfile>(AUTH_ENDPOINTS.me);
    return response.data;
  },

  async deleteAccount(): Promise<void> {
    await api.delete(AUTH_ENDPOINTS.deleteMe);
  },

  async sendOtp(payload: SendOtpPayload): Promise<void> {
    await api.post(AUTH_ENDPOINTS.sendOtp, payload);
  },

  async verifyOtp(payload: VerifyOtpPayload): Promise<void> {
    await api.post(AUTH_ENDPOINTS.verifyOtp, payload);
  },

  async refreshToken(): Promise<string> {
    const response = await api.post<AuthResponse>(AUTH_ENDPOINTS.refresh);
    const { access_token } = response.data;
    localStorage.setItem('access_token', access_token);
    return access_token;
  },

  async logout(): Promise<void> {
    try {
      await api.post(AUTH_ENDPOINTS.logout);
    } catch (error) {
      console.error("Failed to invalidate token on server:", error);
    } finally {
      localStorage.removeItem('access_token');
    }
  },

  async logoutAll(): Promise<void> {
    try {
      await api.post(AUTH_ENDPOINTS.logoutAll);
    } catch (error) {
      console.error("Failed to logout from all devices on server:", error);
    } finally {
      localStorage.removeItem('access_token');
    }
  },

  async getUserSessions(): Promise<UserSession[]> {
    const response = await api.get<UserSession[]>(AUTH_ENDPOINTS.sessions);
    return response.data;
  },

  async forgotPassword(payload: ForgotPasswordPayload): Promise<void> {
    await api.post(AUTH_ENDPOINTS.forgotPassword, payload);
  },

  async verifyResetCode(payload: VerifyResetCodePayload): Promise<void> {
    await api.post(AUTH_ENDPOINTS.verifyResetCode, payload);
  },

  async resetPassword(payload: ResetPasswordPayload): Promise<void> {
    await api.post(AUTH_ENDPOINTS.resetPassword, payload);
  },

  async getUserEglises(userId: number): Promise<Eglise[]> {
    // API returns full user object with eglises array nested inside
    const response = await api.get<{ eglises: Eglise[] }>(`${AUTH_ENDPOINTS.users}/${userId}/eglises`);
    return response.data.eglises || [];
  },

  getToken(): string | null {
    return localStorage.getItem('access_token');
  },

  // Profile image upload functions
  async uploadAvatar(file: File, onProgress?: (progress: number) => void): Promise<{ success: boolean; avatar_url: string; thumbnail_url?: string }> {
    console.log('🖼️ [authService] Uploading avatar...');
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<{ success: boolean; avatar_url: string; thumbnail_url?: string }>(
      '/profile/me/avatar',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      }
    );
    console.log('✅ [authService] Avatar uploaded:', response.data);
    return response.data;
  },

  async deleteAvatar(): Promise<void> {
    console.log('🗑️ [authService] Deleting avatar...');
    await api.delete('/profile/me/avatar');
    console.log('✅ [authService] Avatar deleted');
  },

  async uploadCover(file: File, onProgress?: (progress: number) => void): Promise<{ success: boolean; cover_url: string }> {
    console.log('🖼️ [authService] Uploading cover...');
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<{ success: boolean; cover_url: string }>(
      '/profile/me/cover',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      }
    );
    console.log('✅ [authService] Cover uploaded:', response.data);
    return response.data;
  },

  async deleteCover(): Promise<void> {
    console.log('🗑️ [authService] Deleting cover...');
    await api.delete('/profile/me/cover');
    console.log('✅ [authService] Cover deleted');
  },

  async getMyProfile(): Promise<{ user: any; profile: { avatar_url?: string; cover_url?: string } }> {
    console.log('👤 [authService] Fetching full profile...');
    const response = await api.get<{ user: any; profile: { avatar_url?: string; cover_url?: string } }>('/profile/me');
    console.log('✅ [authService] Full profile fetched:', response.data);
    return response.data;
  },
};

export default authService;
